import { renderErrorAlert, renderWarningAlert } from "@whilecat/core/tasks/alerts.ts";
import { BaseTask } from "@whilecat/core/tasks/base-task.ts";
import { css, html, nothing } from "lit";
import "@whilecat/core/editors/code-area.ts";
import { viewJavaEditorSettings } from "@whilecat/core/editors/code-editor-fabric.js";
import { QuizParamsValidator, type QuizTaskParams } from "@whilecat/core/tasks/common/params/quiz-task-params.js";
import { isBlank, isNotBlank } from "@whilecat/core/utils/string.ts";
import WhilecatStyle from "@whilecat/core/whilecat-style.js";
import { customElement, property } from "lit/decorators.js";

@customElement("quiz-task")
export class QuizTask extends BaseTask<QuizTaskParams> {
    static styles = [
        WhilecatStyle,
        super.styles,
        css`
          :host {
            width: 100%;
          }

          .container {
            display: flex;
            flex-direction: column;
            gap: 24px;
          }

          sl-button {
            width: 100%;
            padding: 2px;
          }

          sl-button.correct-option::part(base) {
            background: var(--whilecat-success-color-1);
            color: black;
          }

          sl-button.error-option::part(base) {
            border: 2px solid var(--whilecat-error-color);
          }

          sl-button.almost-true-option::part(base) {
            border: 2px solid var(--whilecat-warning-color);
          }

        `,
    ];

    @property() private message: string | null = null;
    @property({ type: Boolean }) private error: boolean = false;

    private paramsValidator = new QuizParamsValidator();
    private selectedAnswer: string = "";

    static html(taskId: string, data: QuizTaskParams, completed: boolean, repetitionMode: boolean) {
        return html`
          <quiz-task
                  .taskId=${taskId}
                  .data=${data}
                  .completed="${completed}"
                  .repetitionMode="${repetitionMode}">
        </quiz-task>`;
    }

    override verify() {
        this.error = this.selectedAnswer !== this.data!.correctAnswer;
        this.message = null;

        const almostTrueOption = this.data!.almostTrueOptions?.get(this.selectedAnswer) ?? null;
        if (almostTrueOption !== null) {
            this.message = almostTrueOption;
        }

        if (!this.error) {
            super.publishCompleted();
        }
        this.requestUpdate();
    }

    private onAnswerSelected(answer: string) {
        this.selectedAnswer = answer;
        this.verify();
    }

    renderOption(answer: string) {
        let variant = "default";

        if (this.selectedAnswer) {
            if (this.selectedAnswer === answer) {
                if (this.completed) {
                    variant = "correct-option";
                } else if (this.message != null) {
                    variant = "almost-true-option";
                } else {
                    variant = "error-option";
                }
            }
        }

        return html`
          <sl-button
                  size="small"
                  class="option ${variant}"
                  variant="default"
                  @click="${() => this.onAnswerSelected(answer)}"
                  ?disabled="${this.completed}"
          >
            ${answer}
          </sl-button>
        `;
    }

    override showCorrectAnswer() {
        this.selectedAnswer = this.data!.correctAnswer;
        this.verify();
    }

    override _completeTask() {
        this.showCorrectAnswer();
    }

    renderMessage() {
        if (!this.error) return nothing;

        if (isNotBlank(this.message)) return renderWarningAlert(this.message!, "");

        return this.error ? renderErrorAlert() : nothing;
    }

    protected validateData() {
        this.paramsValidator.validate(this.data!);
    }

    private getCodeAreaElement() {
        if (isBlank(this.data.code)) {
            return nothing;
        }

        return html`
          <code-area .settings="${viewJavaEditorSettings(this.data.code!)}"></code-area>
        `;
    }

    override render() {
        return html`
          <div class="container">
            <p>${this.data!.question}</p>
            ${this.data!.content}
            ${this.getCodeAreaElement()}
            <div class="options">
              ${this.data!.options.map(answer => this.renderOption(answer))}
            </div>
          </div>
          </br>
          ${this.renderMessage()}
        `;
    }
}
